import React from "react";
import { Link } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PLANS, isLaunchOfferActive, LAUNCH_OFFER_LABEL } from "@/lib/plans";
import PlanPrice from "@/components/PlanPrice";

const FEATURE_ROWS = [
  { label: "Connected platforms", values: ["1", "2", "3", "3"] },
  { label: "Products & integrations", values: ["5", "15", "30+", "Unlimited"] },
  { label: "Data extraction", values: [true, true, true, true] },
  { label: "Team seats", values: ["3", "10", "25", "Unlimited"] },
  { label: "Governance policies", values: ["Basic", "Standard", "Advanced", "Advanced"] },
  { label: "Real-time alerts", values: ["Email only", true, true, true] },
  { label: "EMIE Co-pilot", values: [false, false, true, true] },
  { label: "Bring your own LLM", values: [false, false, true, true] },
  { label: "White-label brand + domain", values: [false, false, false, true] },
  { label: "Click-to-deploy (any server, images only)", values: [false, false, false, true] },
  { label: "AES-256 encryption", values: [true, true, true, true] },
  { label: "SHA-256 audit chain", values: [false, true, true, true] },
  { label: "SOC-2 ready", values: [true, true, true, true] },
  { label: "Support", values: ["Community", "Email", "Priority", "Dedicated"] },
];

const FAQS = [
  {
    q: "Do I need a credit card to start?",
    a: "No. Every plan comes with a 7-day free trial and requires no card to get started.",
  },
  {
    q: "Can I change plans later?",
    a: "Yes. You can upgrade or downgrade at any time from your workspace — changes take effect immediately.",
  },
  {
    q: "How many platforms can I connect?",
    a: "Pravadh GovernX supports Salesforce, Zoho and Oracle ecosystems. Higher tiers unlock more connected platforms and products.",
  },
  {
    q: "What is EMIE Co-pilot?",
    a: "EMIE is your built-in governance assistant that lets you query, explain and act on your data ecosystem in plain language. It's included on Enterprise and Custom. Those plans can also point EMIE at your own Anthropic, OpenAI, or OpenAI-compatible model.",
  },
  {
    q: "Can we white-label GovernX on our own servers?",
    a: "Yes on Custom. You run private images on any server you own — Linux VM, Kubernetes, Podman, or another OCI runtime. You never receive source, and you never copy keys from Pravadh HQ. Generate JWT, Fernet, and your LLM key on that box. The step-by-step handbook is at /deploy. If a subscription lapses, users can still sign in read-only.",
  },
  {
    q: "What is the launch offer?",
    a: `Until ${LAUNCH_OFFER_LABEL}, every plan is at a launch price (Starter $59, Classic $99, Enterprise $149, Custom $199). After that date, list prices apply: $99, $149, $199, and $249 per month.`,
  },
  {
    q: "Is my data secure?",
    a: "All data is encrypted with AES-256, protected by a SHA-256 audit chain, and our platform is SOC-2 ready. See our Security page for details.",
  },
];

function Cell({ value }) {
  if (value === true)
    return (
      <span className="inline-flex items-center justify-center text-primary">
        <Check className="h-5 w-5" strokeWidth={2.5} />
      </span>
    );
  if (value === false)
    return (
      <span className="inline-flex items-center justify-center text-muted-foreground/40">
        <Minus className="h-4 w-4" />
      </span>
    );
  return <span className="text-sm font-medium text-foreground">{value}</span>;
}

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center lg:px-8 lg:py-24">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Pricing
          </p>
          <h1 className="mx-auto max-w-3xl font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            One plan for every stage of governance
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
            Simple monthly pricing. Every plan includes a 7-day free trial — no card required.
          </p>
          {isLaunchOfferActive() && (
            <p
              className="mx-auto mt-5 inline-flex rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-sm font-semibold text-amber-700 dark:text-amber-400"
              data-testid="launch-offer-banner"
            >
              Launch offer · list prices return after {LAUNCH_OFFER_LABEL}
            </p>
          )}
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16" data-testid="comparison-table">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="overflow-x-auto pt-4">
            <table className="w-full min-w-[820px] border-collapse">
              <thead>
                <tr>
                  <th className="w-[24%] p-4 text-left align-bottom">
                    <span className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                      Features
                    </span>
                  </th>
                  {PLANS.map((p) => (
                    <th
                      key={p.id}
                      className={`relative w-[19%] rounded-t-2xl border-x border-t p-6 text-left align-top ${
                        p.popular ? "border-primary bg-primary/5" : "border-border bg-card/40"
                      }`}
                      data-testid={`pricing-col-${p.id}`}
                    >
                      {p.popular && (
                        <span className="absolute -top-3 left-6 rounded-full bg-accent px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                          Popular
                        </span>
                      )}
                      <p className="font-display text-lg font-bold tracking-tight">{p.name}</p>
                      <PlanPrice plan={p} size="lg" className="mt-2" />
                      <Link to="/signup">
                        <Button
                          size="sm"
                          variant={p.popular ? "default" : "outline"}
                          className="mt-4 w-full font-semibold"
                          data-testid={`pricing-cta-${p.id}`}
                        >
                          Start trial
                        </Button>
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_ROWS.map((row, ri) => (
                  <tr key={row.label} className="border-t border-border">
                    <td className="p-4 text-sm font-medium text-muted-foreground">{row.label}</td>
                    {row.values.map((v, vi) => {
                      const popular = PLANS[vi].popular;
                      const last = ri === FEATURE_ROWS.length - 1;
                      return (
                        <td
                          key={vi}
                          className={`p-4 text-center align-middle border-x ${
                            popular ? "border-primary bg-primary/5" : "border-transparent"
                          } ${last && popular ? "rounded-b-2xl border-b" : ""}`}
                        >
                          <Cell value={v} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border bg-card/40 py-20" data-testid="faq-section">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="mb-10 text-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              FAQ
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Frequently asked questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display text-base font-bold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
